import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useEffect, useRef, useState } from "react";

interface ButtonGroupOption {
  value: string;
  label: React.ReactNode;
  disabled?: boolean;
}

interface ButtonGroupProps {
  options: Array<ButtonGroupOption>;
  value: string;
  onChange: (value: string) => void;
  size?: "sm" | "default" | "lg";
  className?: string;
  disabled?: boolean;
}

export function ButtonGroup({
  options,
  value,
  onChange,
  size = "sm",
  className = "",
  disabled = false,
}: ButtonGroupProps) {
  const buttonRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const [indicatorStyle, setIndicatorStyle] = useState<{
    left: number;
    width: number;
  }>({ left: 0, width: 0 });

  useEffect(() => {
    const activeButton = buttonRefs.current.get(value);
    if (activeButton) {
      const container = activeButton.parentElement;
      if (container) {
        const containerRect = container.getBoundingClientRect();
        const buttonRect = activeButton.getBoundingClientRect();
        setIndicatorStyle({
          left: buttonRect.left - containerRect.left,
          width: buttonRect.width,
        });
      }
    }
  }, [value, options]);

  return (
    <div
      className={cn(
        "button-group-container flex items-center gap-1 relative",
        className,
      )}
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      {/* Animated indicator */}
      <div
        className="button-group-active-indicator absolute pointer-events-none"
        style={{
          left: `${indicatorStyle.left}px`,
          width: `${indicatorStyle.width}px`,
        }}
      />

      {options.map((option) => (
        <Button
          ref={(el) => {
            if (el) {
              buttonRefs.current.set(option.value, el);
            } else {
              buttonRefs.current.delete(option.value);
            }
          }}
          variant="ghost"
          key={option.value}
          size={size}
          className={cn(
            "cursor-pointer text-muted-foreground bg-transparent hover:text-foreground relative z-10",
            value === option.value
              ? "text-foreground hover:bg-transparent dark:hover:bg-transparent"
              : "shadow-none hover:bg-transparent dark:hover:bg-transparent",
          )}
          onClick={(e) => {
            if (disabled || option.disabled === true) {
              return;
            }
            e.stopPropagation();
            e.preventDefault();
            onChange(option.value);
          }}
          disabled={disabled || option.disabled === true}
        >
          {option.label}
        </Button>
      ))}
    </div>
  );
}
